import React, { useRef } from 'react';
import { motion, useInView } from 'framer-motion';
import { FiAward, FiBook } from 'react-icons/fi';

const achievements = [
  {
    title: 'National Level 2nd Runner-Up',
    event: 'Pitch It UP! 2.0',
    org: 'MSCW Delhi University, New Delhi',
    type: 'National',
    color: 'primary',
  },
  {
    title: 'National Level 2nd Runner-Up',
    event: 'Idea Ignite',
    org: 'MNIT Jaipur University, Rajasthan',
    type: 'National',
    color: 'primary',
  },
  {
    title: 'State Level 1st Prize',
    event: 'Project Expo',
    org: 'Brindavan College of Engineering, Bengaluru',
    type: 'State',
    color: 'accent',
  },
  {
    title: 'Round-1 Qualifier',
    event: 'URSC-ISRO Robotics Challenge 2024',
    org: 'ISRO, India',
    type: 'National',
    color: 'blue',
  },
  {
    title: 'Best Outgoing Student of CSE 2024',
    event: 'Awarded by Anurag Thakur, MLA of Lok Sabha',
    org: 'Brindavan College of Engineering',
    type: 'Institute',
    color: 'yellow',
  },
];

const publications = [
  {
    title: 'Engineering the Future: Cutting-Edge Phone Technology with Water, Heat, and Velocity Resistance',
    journal: 'Research Publication',
  },
  {
    title: 'Resurrecting Shadows: Overcoming Power-Off Boundaries With GPS-Based Tracking For Switched-Off Smartphones',
    journal: 'Research Publication',
  },
];

const certifications = [
  { title: 'Data Science and Analytics', org: 'Karnataka State Council for Science and Technology (KSCST)' },
  { title: 'Machine Learning using Python', org: 'KSCST' },
  { title: 'AI and Machine Learning Full Course', org: 'Simplilearn' },
  { title: 'Python Programming', org: 'IIT Madras' },
  { title: 'Certified Cyber Forensics Professional (CCFP)', org: 'Certification Body' },
  { title: 'AI For India 2.0', org: 'GUVI / Google' },
];

const colorConfig = {
  primary: {
    bg: 'bg-primary-50',
    text: 'text-primary-600',
    badge: 'bg-primary-100 text-primary-700',
    icon: 'text-primary-500',
    border: 'border-primary-100',
  },
  accent: {
    bg: 'bg-accent-50',
    text: 'text-accent-600',
    badge: 'bg-accent-100 text-accent-700',
    icon: 'text-accent-500',
    border: 'border-accent-100',
  },
  blue: {
    bg: 'bg-blue-50',
    text: 'text-blue-600',
    badge: 'bg-blue-100 text-blue-700',
    icon: 'text-blue-500',
    border: 'border-blue-100',
  },
  yellow: {
    bg: 'bg-amber-50',
    text: 'text-amber-600',
    badge: 'bg-amber-100 text-amber-700',
    icon: 'text-amber-500',
    border: 'border-amber-100',
  },
};

function AnimateIn({ children, delay = 0 }) {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: '-40px' });
  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: 25 }}
      animate={inView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.55, delay }}
    >
      {children}
    </motion.div>
  );
}

export default function Achievements() {
  return (
    <section id="achievements" className="bg-white">
      <div className="section-container space-y-20">
        {/* Achievements */}
        <div>
          <AnimateIn>
            <div className="flex items-center gap-3 mb-3">
              <div className="w-1 h-8 bg-gradient-to-b from-primary-600 to-accent-500 rounded-full" />
              <p className="text-primary-600 font-semibold text-sm uppercase tracking-widest">Recognition</p>
            </div>
            <h2 className="section-title">Achievements & Awards</h2>
            <p className="text-slate-400 text-sm mb-10">Competing at national stage and winning recognition for innovation.</p>
          </AnimateIn>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {achievements.map((a, i) => {
              const cfg = colorConfig[a.color];
              return (
                <AnimateIn key={i} delay={i * 0.08}>
                  <div className={`card p-5 border ${cfg.border} h-full`}>
                    <div className="flex items-start gap-3">
                      <div className={`w-10 h-10 rounded-xl ${cfg.bg} flex items-center justify-center flex-shrink-0`}>
                        <FiAward size={20} className={cfg.icon} />
                      </div>
                      <div>
                        <span className={`text-xs font-bold px-2 py-0.5 rounded-full ${cfg.badge} inline-block mb-1`}>
                          {a.type} Level
                        </span>
                        <h3 className="font-semibold text-sm text-slate-800 leading-tight">{a.title}</h3>
                        <p className={`text-xs font-medium mt-0.5 ${cfg.text}`}>{a.event}</p>
                        <p className="text-xs text-slate-500 mt-0.5">{a.org}</p>
                      </div>
                    </div>
                  </div>
                </AnimateIn>
              );
            })}
          </div>
        </div>

        {/* Research & Publications */}
        <div>
          <AnimateIn>
            <div className="flex items-center gap-3 mb-3">
              <div className="w-1 h-8 bg-gradient-to-b from-blue-500 to-primary-600 rounded-full" />
              <p className="text-blue-600 font-semibold text-sm uppercase tracking-widest">Research</p>
            </div>
            <h2 className="section-title">Research & Publications</h2>
            <p className="text-slate-400 text-sm mb-8">Contributing to academic and applied research in technology.</p>
          </AnimateIn>

          <div className="space-y-4">
            {publications.map((pub, i) => (
              <AnimateIn key={i} delay={i * 0.1}>
                <div className="card p-5 flex items-start gap-4">
                  <div className="w-10 h-10 rounded-xl bg-blue-50 flex items-center justify-center flex-shrink-0">
                    <FiBook size={18} className="text-blue-600" />
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-slate-800 leading-snug">{pub.title}</p>
                    <p className="text-xs text-slate-500 mt-1 font-medium">{pub.journal}</p>
                  </div>
                </div>
              </AnimateIn>
            ))}
          </div>
        </div>

        {/* Certifications */}
        <div>
          <AnimateIn>
            <div className="flex items-center gap-3 mb-3">
              <div className="w-1 h-8 bg-gradient-to-b from-accent-500 to-blue-500 rounded-full" />
              <p className="text-accent-600 font-semibold text-sm uppercase tracking-widest">Learning</p>
            </div>
            <h2 className="section-title">Certifications</h2>
            <p className="text-slate-400 text-sm mb-8">Continuous learning from industry-leading institutions.</p>
          </AnimateIn>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {certifications.map((cert, i) => (
              <AnimateIn key={i} delay={i * 0.06}>
                <div className="card p-4 flex items-start gap-3 group hover:border-primary-200">
                  <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-primary-100 to-accent-100 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <span className="text-xs font-bold text-primary-700">{i + 1}</span>
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-slate-800 leading-tight">{cert.title}</p>
                    <p className="text-xs text-slate-500 mt-0.5">{cert.org}</p>
                  </div>
                </div>
              </AnimateIn>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
